// src/components/PrivacyPolicy.jsx
import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mt-20 mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
         <div className="mt-4 px-4 py-1 bg-orange-50 rounded-lg inline-block">
            <p className="text-orange-800 text-sm font-medium">
              Last updated: <span className='text-gray-600 text-md font-bold'>December 06, 2025</span>
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                Introduction
              </h2>
              <p className="text-gray-700 mb-4">
                Welcome to CC Technology. We are committed to protecting your privacy and ensuring 
                the security of your personal information. This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you visit our website.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                Information We Collect
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Personal Information</h3>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Name and contact details</li>
                    <li>• Email address</li>
                    <li>• Phone number</li>
                    <li>• Company information</li>
                    <li>• Payment information</li>
                  </ul>
                </div>
                
                <div className="bg-green-50 p-6 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Technical Information</h3>
                  <ul className="text-gray-700 space-y-2">
                    <li>• IP address and browser type</li>
                    <li>• Device information</li>
                    <li>• Cookies and usage data</li>
                    <li>• Website analytics</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                How We Use Your Information
              </h2>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Service Provision</h4>
                    <ul className="text-gray-700 space-y-2 text-sm">
                      <li>• Process your requests and orders</li>
                      <li>• Provide customer support</li>
                      <li>• Send service updates</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Business Operations</h4>
                    <ul className="text-gray-700 space-y-2 text-sm">
                      <li>• Improve our services</li>
                      <li>• Analyze website usage</li>
                      <li>• Marketing communications</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                Data Sharing and Disclosure
              </h2>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-6">
                <p className="text-gray-700">
                  We do not sell your personal information. We may share your data with:
                </p>
              </div>
              
              <ul className="text-gray-700 space-y-3">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Service Providers:</strong> Trusted partners who assist in our operations</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Legal Requirements:</strong> When required by law or legal process</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>Business Transfers:</strong> In connection with mergers or acquisitions</span>
                </li>
              </ul>
            </section>

            {/* Data Security */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                Data Security
              </h2>
              
              <div className="bg-green-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-4">
                  We implement appropriate technical and organizational security measures to protect 
                  your personal information against unauthorized access, alteration, disclosure, or destruction.
                </p>
                <div className="grid md:grid-cols-3 gap-4 text-center">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="w-8 h-8 bg-green-500 rounded-full mx-auto mb-2 flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                    <p className="text-sm font-medium">Encryption</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="w-8 h-8 bg-green-500 rounded-full mx-auto mb-2 flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                    <p className="text-sm font-medium">Access Controls</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="w-8 h-8 bg-green-500 rounded-full mx-auto mb-2 flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                    <p className="text-sm font-medium">Regular Audits</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                Your Rights
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                      <span className="text-blue-600 text-sm">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Access & Correction</h4>
                      <p className="text-gray-700 text-sm">Right to access and update your information</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                      <span className="text-blue-600 text-sm">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Data Portability</h4>
                      <p className="text-gray-700 text-sm">Right to receive your data in a readable format</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                      <span className="text-blue-600 text-sm">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Deletion</h4>
                      <p className="text-gray-700 text-sm">Right to request deletion of your data</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
                      <span className="text-blue-600 text-sm">4</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Objection</h4>
                      <p className="text-gray-700 text-sm">Right to object to certain processing activities</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-blue-50 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <p>Email: contact@cctechnology.com.np</p>
                <p>Phone: +977-9819931223</p>
                <p>Address: Janakpur, Nepal</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;